// Pokémon Showdown's sprite filename conventions are inconsistent across
// formes — sometimes "charizard-mega-y" (hyphenated as the species id),
// sometimes "charizard-megay" (one hyphen collapsed), sometimes
// "charizardmegay" (no hyphens at all in the dex sprite folder). The design
// hand-off calls this out explicitly and asks for a 5-level fallback chain.
//
// We build all candidate URLs up front and let the Sprite component swap to
// the next one on each 404.

const SHOWDOWN = 'https://play.pokemonshowdown.com/sprites';

function normalizeSlug(species: string): string {
  return species.trim().toLowerCase().replace(/\s+/g, '-');
}

function collapseLastHyphen(slug: string): string {
  // "charizard-mega-y" → "charizard-megay" (collapses the LAST internal hyphen).
  // Only applies when there are 2+ hyphens — single-hyphen names like
  // "floette-eternal" stay intact because Showdown's gen5 file uses the
  // hyphenated form for those.
  const hyphens = (slug.match(/-/g) ?? []).length;
  if (hyphens < 2) return slug;
  const idx = slug.lastIndexOf('-');
  if (idx <= 0 || idx === slug.length - 1) return slug;
  return slug.slice(0, idx) + slug.slice(idx + 1);
}

function noHyphens(slug: string): string {
  return slug.replace(/[-.]/g, '');
}

export function buildSpriteUrlChain(species: string): string[] {
  const slug = normalizeSlug(species);
  const collapsed = collapseLastHyphen(slug);
  const flat = noHyphens(slug);

  // Showdown's gen5 sprite folder uses the @pkmn-id style (no hyphens) for
  // alternate formes — `charizardmegay.png`, `froslassmega.png`. Some formes
  // also resolve via the hyphenated or partially-collapsed name. We try all
  // three variants per folder; the chain is short enough that the 404 cost
  // is invisible to the user.
  const candidates = [
    `${SHOWDOWN}/gen5/${flat}.png`,
    `${SHOWDOWN}/gen5/${collapsed}.png`,
    `${SHOWDOWN}/gen5/${slug}.png`,
    `${SHOWDOWN}/dex/${slug}.png`,
    `${SHOWDOWN}/dex/${flat}.png`,
    `${SHOWDOWN}/home/${slug}.png`,
  ];

  // Dedupe while preserving order.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of candidates) {
    if (!seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}
